"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, FileText, Compass, ArrowUpRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const principles = [{
  id: "01",
  title: "Zero sponsored bias",
  description: "Every research report and market forecast is funded exclusively through direct subscriptions and our own editorial budget — never paid vendor placements."
}, {
  id: "02",
  title: "Peer-reviewed methodology",
  description: "Our analysts test qualitative findings against structured quantitative datasets before publishing, working alongside domain practitioners in the field."
}, {
  id: "03",
  title: "Transparent source data",
  description: "We document research boundaries, survey sample sizes, and analytical assumptions so decision-makers can verify every metric they cite."
}];
const editorialStats = [{
  label: "Active executive readers",
  value: "48,000+"
}, {
  label: "Published research briefs",
  value: "320+"
}, {
  label: "Independent analyst contributors",
  value: "65"
}];
export default function Story() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="our-story-mission" data-nav="light" className="relative bg-background text-foreground py-20 sm:py-24 lg:py-28 overflow-hidden border-b border-border">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
        {/* Editorial Masthead Rule */}
        <div className="w-full mb-10 sm:mb-14">
          <div className="h-px bg-foreground/20 w-full mb-1" />
          <div className="h-0.5 bg-foreground/10 w-full" />
          <div className="flex items-center justify-between pt-3 text-xs tracking-widest uppercase font-mono text-muted-foreground">
            <span>MinfoMedia Editorial Foundation</span>
            <span>Est. 2018 · Springfield, USA</span>
          </div>
        </div>

        {/* Top Story Split */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14 items-start mb-20">
          {/* Main Narrative Column */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="lg:col-span-7 flex flex-col gap-6">
            <motion.div variants={itemVariants}>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider">
                <Compass className="size-3.5 text-foreground" />
                Our origin & purpose
              </span>
            </motion.div>

            <motion.h2 variants={itemVariants} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-[1.18]">
              Independent research built for high-stakes decisions.
            </motion.h2>

            <motion.div variants={itemVariants} className="space-y-5 text-base sm:text-lg leading-relaxed text-foreground/85">
              <p>
                MinfoMedia began with a simple observation: modern business leaders are flooded with promotional whitepapers, yet starved for rigorous, uncompromised market analysis. Most industry reports exist to generate enterprise sales leads for their sponsors.
              </p>
              <p>
                We built MinfoMedia as a respected business journal and research institution. We do not accept sponsored conclusions, pay-to-play vendor evaluations, or hidden brand integrations. Our analysts assess market shifts, operating models, and technology adoptions with clear data and neutral scrutiny.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="pt-2 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/ResearchReportsPage">
                  Browse research library
                  <ArrowUpRight className="ml-1 size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-sm border-border bg-card text-foreground hover:bg-muted">
                <Link to="/IndustryInsightsPage">
                  Read industry insights
                </Link>
              </Button>
            </motion.div>
          </motion.div>

          {/* Right Image & Trust Card Column */}
          <motion.div initial={{
          opacity: 0,
          y: prefersReducedMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-5 flex flex-col gap-6">
            {/* Authentic Editorial Photo */}
            <div className="relative rounded-md overflow-hidden border border-border bg-card shadow-sm">
              <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/editorial_team_meeting.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/editorial_team_meeting.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/editorial_team_meeting.png-wvc-sizes" alt="MinfoMedia senior research analysts and editorial board collaborating on quarterly market report" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
              <div className="p-4 bg-card border-t border-border flex items-start gap-3 text-xs text-muted-foreground">
                <ShieldCheck className="size-4 shrink-0 text-foreground mt-0.5" />
                <p>
                  MinfoMedia analysts and contributing editors review market data prior to publication at our Springfield headquarters.
                </p>
              </div>
            </div>

            {/* Quick Fact Metrics Strip */}
            <div className="grid grid-cols-3 gap-3 p-4 rounded-md bg-card border border-border">
              {editorialStats.map((stat, i) => <div key={stat.label} data-index={i} className="flex flex-col">
                  <span className="font-serif text-xl sm:text-2xl font-bold text-foreground">
                    {stat.value}
                  </span>
                  <span className="text-[11px] sm:text-xs text-muted-foreground leading-tight mt-1">
                    {stat.label}
                  </span>
                </div>)}
            </div>
          </motion.div>
        </div>

        {/* Editorial Standards & Principles */}
        <motion.div initial={{
        opacity: 0,
        y: prefersReducedMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.15
      }} transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="pt-10 border-t border-border">
          <div className="max-w-2xl mb-8">
            <span className="text-xs uppercase font-mono tracking-wider text-muted-foreground">
              Core Publishing Tenets
            </span>
            <h3 className="font-serif text-2xl sm:text-3xl font-semibold text-foreground mt-1">
              How we protect editorial integrity
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {principles.map((item, index) => <Card key={item.id} data-index={index} className="bg-card border-border rounded-md shadow-2xs hover:border-foreground/30 transition-[border-color,box-shadow] duration-200">
                <CardContent className="p-6 flex flex-col justify-between h-full gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-mono text-xs text-muted-foreground font-semibold">
                        Pillar {item.id}
                      </span>
                      <CheckCircle2 className="size-4 text-foreground/70" />
                    </div>
                    <h4 className="text-lg font-semibold text-foreground mb-2">
                      {item.title}
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                  <div className="pt-3 border-t border-border/60 flex items-center text-xs font-mono text-foreground/80">
                    <FileText className="size-3.5 mr-1.5" />
                    Verified peer process
                  </div>
                </CardContent>
              </Card>)}
          </div>
        </motion.div>
      </div>
    </section>;
}